import { useEffect, useState, type ReactElement } from 'react';
import { trackFailed } from '../../../../../shared-web/track';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { ApiError } from '../../auth/api-client';
import { emberApi, type LetterHandoffResponse } from '../api';
import { useTalkSheet } from '../talk-sheet';
import { LetterDoor, LetterSheet } from '../components/LetterSheet';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import './HandoffPage.css';
import './LetterHandoffPage.css';

/**
 * A weekly letter its reader handed to someone — `/l/:token` (§4.20,
 * 2026-09-24 拍板; the Ember's §4.18 door on the letter). It is the letter
 * itself, the same sheet the reader saw, without the letterhead facts that
 * are the reader's (which letter, what days) and with one line in their
 * place; at its foot one door, the telling door every hand-off page ends in.
 * Nothing about who the letter was written to: no name, no picture, no
 * conversation (§0.9 — the reader's identity does not leave with the link).
 * A token nobody holds reads as exactly that.
 */
export function LetterHandoffPage({ token, initialData = null }: {
  token: string;
  initialData?: LetterHandoffResponse | null;
}): ReactElement {
  const openTalk = useTalkSheet();
  const path = `/l/${encodeURIComponent(token)}`;
  const [data, setData] = useState<LetterHandoffResponse | null>(initialData);
  const [failed, setFailed] = useState<false | 'gone' | 'load_failed'>(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    emberApi.letterHandoff(token)
      .then((response) => { if (alive) setData(response); })
      .catch((err) => {
        if (!alive || initialData) return;
        const gone = err instanceof ApiError && err.status === 404;
        trackFailed('letter_handoff_load', gone ? 'not_found' : 'load_failed');
        setFailed(gone ? 'gone' : 'load_failed');
      });
    return () => { alive = false; };
  }, [token, initialData]);

  const brand = (
    <div className="handoff-toolbar">
      <a href="/" className="handoff-brand" aria-label="Ember home" data-log="page_home">
        <EmberFlame className="handoff-brand-flame" />
        <span>Ember</span>
      </a>
    </div>
  );
  const door = (
    <LetterDoor
      title="Talk with Ember"
      sub="Ember may write one of these each week to the people it talks with."
      log="letter_handoff_tell_open"
      logAttrs={{ 'data-log-letter-id': data?.letter.id }}
      onClick={() => openTalk({ entryType: 'home', returnTo: path })}
    />
  );

  if (failed === 'gone') {
    return (
      <main className="ember-container ember-reading handoff-page letter-handoff">
        {brand}
        <div className="handoff-gone" data-log-seen="letter_handoff_seen" data-log-state="gone">
          <h1>This letter isn’t here.</h1>
          <p>The link may be old, or mistyped.</p>
        </div>
        <section className="handoff-door" aria-labelledby="letter-handoff-door-title">
          <h2 id="letter-handoff-door-title" className="handoff-door-title"><EmberFlame className="handoff-door-flame" />Ember writes to the people it talks with.</h2>
          <p className="handoff-door-text">Once a week, Ember reads back over what you’ve written together and may write you one letter — what it sees, in your own words.</p>
          <div className="handoff-door-foot">
            <button type="button" className="handoff-door-cta" data-log="letter_handoff_tell_open" onClick={() => openTalk({ entryType: 'home', returnTo: path })}>Talk with Ember</button>
            <span className="handoff-door-note">Type or talk. No account needed.</span>
          </div>
        </section>
      </main>
    );
  }
  if (failed) return <PageFailure title="Couldn’t open this letter" detail="Refresh to try again." logState="load_failed" />;
  if (!data) return <PageLoading />;

  return (
    <main className="ember-container ember-reading handoff-page letter-handoff">
      {brand}
      <LetterSheet
        letter={data.letter}
        facts={<p className="letter-handoff-fact">Sent to you by someone who talks with Ember</p>}
        logSeen="letter_handoff_seen"
        logSeenAttrs={{ 'data-log-letter-id': data.letter.id }}
      >
        {door}
      </LetterSheet>
    </main>
  );
}
